import { useMutation, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";

import type { AugmentedShipment } from "~/features/shipment-management/hooks/use-get-shipment.ts";
import { useShipmentNotifications } from "~/features/shipment-management/hooks/use-shipment-notifications.ts";
import { groupRemarksOrCheckCallsByDate } from "~/features/shipment-management/utils/group-remarks-by-date.ts";
import type { ShipmentRemark } from "~/shared/schemas/shipment-management-schema.ts";
import { apiRoutes } from "~/shared/services/api/api-routes.ts";
import { deleteAndValidate } from "~/shared/services/api/fetch-client.ts";

export const deleteRemark = async (messageData: {
	remark: ShipmentRemark;
	poNumber: string;
}) => {
	const url = apiRoutes.PUTDELETEREMARK.replace(
		":poNumber",
		messageData.poNumber,
	).replace(":remarkId", messageData.remark.id?.toString() ?? "");
	const resp = await deleteAndValidate(url, z.unknown());

	if (resp.success) {
		return { remarkId: messageData.remark.id, data: resp.data };
	}

	throw new Error(resp.error);
};

export const useDeleteRemark = () => {
	const queryClient = useQueryClient();
	const { showSuccess, showError } = useShipmentNotifications();

	const { mutate, mutateAsync, isPending, error, data, reset } = useMutation({
		mutationFn: (messageData: { remark: ShipmentRemark; poNumber: string }) =>
			deleteRemark(messageData),
		onSuccess: (savedRemark) => {
			queryClient.setQueriesData<AugmentedShipment>(
				{ queryKey: ["shipment"] },
				(old) => {
					if (!old) {
						return old;
					}
					const remarks = (old.remarks ?? []).filter(
						(remark) => remark.id !== savedRemark.remarkId,
					);
					return {
						...old,
						remarks,
						groupedRemarks: groupRemarksOrCheckCallsByDate(remarks),
					};
				},
			);
			showSuccess("Remark deleted successfully.");
		},
		onError: () => {
			showError("Failed to delete remark. Please try again.");
		},
	});

	return {
		deleteRemark: mutate,
		deleteRemarkAsync: mutateAsync,
		isDeletingRemark: isPending,
		error,
		data,
		resetError: reset,
	};
};
